import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Database, Download, ArrowsClockwise, Trash, CheckCircle, Clock
} from '@phosphor-icons/react';

export default function CmsBackupRestore() {
  const [backups, setBackups] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [backupName, setBackupName] = useState('');

  useEffect(() => {
    loadBackups();
    loadStats();
  }, []);

  const loadBackups = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backups`, {
        withCredentials: true,
      });
      setBackups(r.data.backups || []);
    } catch (e) {
      toast.error('Failed to load backups');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backups/stats`, {
        withCredentials: true,
      });
      setStats(r.data);
    } catch (e) {
      console.error('Failed to load stats');
    }
  };

  const createBackup = async () => {
    if (!backupName.trim()) {
      toast.error('Please enter a backup name');
      return;
    }

    setCreating(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/backups/create?name=${encodeURIComponent(backupName)}`,
        {},
        { withCredentials: true }
      );
      toast.success('Backup started in background');
      setBackupName('');
      setTimeout(loadBackups, 5000); // Refresh after 5s
    } catch (e) {
      toast.error('Failed to start backup');
    } finally {
      setCreating(false);
    }
  };

  const deleteBackup = async (backupId) => {
    if (!window.confirm('Delete this backup?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backups/${backupId}`, {
        withCredentials: true,
      });
      toast.success('Backup deleted');
      loadBackups();
      loadStats();
    } catch (e) {
      toast.error('Failed to delete backup');
    }
  };

  return (
    <div className="space-y-6" data-testid="cms-backup-restore">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Database size={28} weight="duotone" className="text-primary" />
          Backup & Restore
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Create database backups, restore from previous versions, and schedule automated backups
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Total Backups</p>
            <p className="text-2xl font-bold">{stats.total_backups}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Completed</p>
            <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Failed</p>
            <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Storage Used</p>
            <p className="text-2xl font-bold">{stats.total_size_mb} MB</p>
          </div>
        </div>
      )}

      {/* Create Backup */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold mb-3">Create New Backup</h3>
        <div className="flex items-center gap-3">
          <input
            value={backupName}
            onChange={(e) => setBackupName(e.target.value)}
            placeholder="Backup name (e.g., Before major update)"
            className="flex-1 px-3 py-2 text-sm bg-background border border-border rounded"
            data-testid="backup-name-input"
          />
          <button
            onClick={createBackup}
            disabled={creating}
            className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-lg font-semibold disabled:opacity-50"
            data-testid="create-backup-btn"
          >
            <ArrowsClockwise size={16} weight={creating ? 'bold' : 'regular'} />
            {creating ? 'Creating...' : 'Create Backup'}
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Backup includes: users, authors, works, articles, categories, settings, and media metadata
        </p>
      </div>

      {/* Backups List */}
      {loading ? (
        <div className="text-center py-12">Loading backups...</div>
      ) : backups.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Database size={48} weight="duotone" className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No backups created yet</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg divide-y divide-border">
          {backups.map((backup) => (
            <div key={backup.backup_id} className="p-4" data-testid={`backup-${backup.backup_id}`}>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold">{backup.name}</p>
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                      backup.status === 'completed' ? 'bg-green-100 text-green-700' :
                      backup.status === 'failed' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {backup.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>{new Date(backup.created_at).toLocaleString()}</span>
                    {backup.size_bytes && (
                      <>
                        <span>·</span>
                        <span>{(backup.size_bytes / (1024 * 1024)).toFixed(2)} MB</span>
                      </>
                    )}
                    {backup.collections && (
                      <>
                        <span>·</span>
                        <span>{backup.collections.length} collections</span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => deleteBackup(backup.backup_id)}
                  className="text-muted-foreground hover:text-red-600 p-2"
                  title="Delete backup"
                >
                  <Trash size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
